import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowLeft, Save, X, Plus, Trash2, Calculator, GripVertical } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { formatCurrency, storage } from '@/utils'
import { materialsApi, projectsApi, apiClient } from '@/services/api'
import { useCurrentTemplate } from '@/hooks/useCurrentTemplate'
import { TemplateDrivenProjectForm } from '@/components/projects/TemplateDrivenProjectForm'
import { ProjectTypeManager } from '@/components/projects/ProjectTypeManager'

interface ProjectMaterial {
  id: string
  materialId: string
  materialName: string
  unitType: string
  quantityNeeded: number
  estimatedCost: number
  actualCost?: number
  notes?: string
}

export const CreateProjectPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditMode = Boolean(id)
  const { template: currentTemplate } = useCurrentTemplate()
  
  const [loading, setLoading] = useState(false)
  const [materialsLoading, setMaterialsLoading] = useState(false)
  const [projectLoading, setProjectLoading] = useState(isEditMode)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: '',
    status: 'planning',
    targetPrice: '',
    laborRate: '25.00',
    estimatedHours: '',
    notes: ''
  })
  const [templateFormData, setTemplateFormData] = useState<Record<string, any>>({})
  const [projectMaterials, setProjectMaterials] = useState<ProjectMaterial[]>([])
  const [showMaterialSelector, setShowMaterialSelector] = useState(false)
  const [availableMaterials, setAvailableMaterials] = useState<Array<{
    id: string
    name: string
    unitType: string
    currentStock: number
    avgCost: number
    category: string
  }>>([])
  const [projectTypes, setProjectTypes] = useState<Array<{
    id: string
    name: string
    description?: string
  }>>([])
  const [showProjectTypeManager, setShowProjectTypeManager] = useState(false)

  // Load project data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      fetchProjectData(id)
    }
  }, [isEditMode, id])

  // Load project types on component mount
  useEffect(() => {
    fetchProjectTypes()
  }, [])

  // Load available materials when material selector is opened
  useEffect(() => {
    if (showMaterialSelector && availableMaterials.length === 0) {
      fetchAvailableMaterials()
    }
  }, [showMaterialSelector])

  const fetchProjectData = async (projectId: string) => {
    try {
      setProjectLoading(true)
      setError(null)

      const project = await projectsApi.getById(projectId)
      console.log('Loaded project data:', project)

      // Update form data with project information
      setFormData({
        name: project.name || '',
        description: project.description || '',
        type: project.templateProjectTypeId || project.type || '',
        status: project.status || 'planning',
        targetPrice: project.targetSellingPrice ? project.targetSellingPrice.toString() : '',
        laborRate: '25.00', // Default labor rate
        estimatedHours: project.estimatedHours ? project.estimatedHours.toString() : '',
        notes: project.designNotes || ''
      })

      // Load project materials if they exist
      if (project.projectMaterials && project.projectMaterials.length > 0) {
        const materials = project.projectMaterials.map((pm: any, index: number) => {
          const totalCost = parseFloat(pm.totalCost) || 0
          const quantityUsed = parseFloat(pm.quantityUsed) || 0
          
          return {
            id: pm.id || `existing-${index}`,
            materialId: pm.materialId || pm.material?.id,
            materialName: pm.material?.name || 'Unknown Material',
            unitType: pm.material?.baseUnit || pm.material?.unitType || 'unit',
            quantityNeeded: quantityUsed,
            estimatedCost: totalCost,
            actualCost: totalCost,
            notes: pm.notes || ''
          }
        })
        console.log('Loaded project materials:', materials)
        setProjectMaterials(materials)
      }
    } catch (error) {
      console.error('Error loading project:', error)
      setError(`Failed to load project: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setProjectLoading(false)
    }
  }

  const fetchProjectTypes = async () => {
    try {
      const token = storage.getToken();
      if (!token) {
        console.error('No authentication token found');
        setProjectTypes([]);
        return;
      }

      const response = await fetch('/api/v1/templates/user/project-types', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setProjectTypes(data || []);
      } else {
        console.error('Failed to fetch project types:', response.status);
        setProjectTypes([]);
      }
    } catch (error) {
      console.error('Error fetching project types:', error)
      setProjectTypes([])
    }
  }

  const fetchAvailableMaterials = async () => {
    try {
      setMaterialsLoading(true)
      setError(null)

      const response = await materialsApi.getAll()
      console.log('Materials API response:', response) // Debug log
      
      // Handle different response structures
      let materials = []
      if (response && Array.isArray(response.data)) {
        materials = response.data
      } else if (Array.isArray(response)) {
        materials = response
      } else if (response && response.materials) {
        materials = response.materials
      }

      console.log('Processed materials:', materials) // Debug log
      
      if (materials.length === 0) {
        setError('No materials found in inventory. Please add materials first.')
        setAvailableMaterials([])
        return
      }

      const processedMaterials = materials
        .filter((material: any) => material && material.id) // Filter out invalid materials
        .map((material: any) => {
          const processed = {
            id: material.id,
            name: material.name || 'Unknown Material',
            unitType: material.baseUnit || material.unitType || 'unit',
            currentStock: Number(material.currentStock) || 0,
            avgCost: Number(material.averageCost) || Number(material.lastPurchasePrice) || Number(material.lastCostPerUnit) || 0,
            category: material.category?.name || material.category || 'Other'
          }
          console.log('Processed material:', processed) // Debug log
          return processed
        })
        .filter((material: any) => material.currentStock > 0) // Only show materials with stock
      
      setAvailableMaterials(processedMaterials)
      
      if (processedMaterials.length === 0) {
        setError('No materials with available stock found. Please check your inventory.')
      }
    } catch (error) {
      console.error('Error fetching materials:', error)
      setError(`Failed to load materials: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setMaterialsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const projectData = {
        name: formData.name,
        templateProjectTypeId: formData.type || undefined,
        type: formData.type ? undefined : 'custom', // Use template type or fallback to 'custom'
        description: formData.description,
        targetSellingPrice: parseFloat(formData.targetPrice) || undefined,
        estimatedHours: parseFloat(formData.estimatedHours) || undefined,
        customMultiplier: 1.5, // Default multiplier
        status: formData.status,
        designNotes: formData.notes ? { notes: formData.notes } : undefined,
        // Include template-specific form data
        templateFormData: Object.keys(templateFormData).length > 0 ? templateFormData : undefined
      }

      let project
      if (isEditMode && id) {
        // Update existing project
        project = await projectsApi.update(id, projectData)
        console.log('Updated project:', project)
      } else {
        // Create new project
        project = await projectsApi.create(projectData)
        console.log('Created project:', project)
      }

      // Add materials only for create mode, not edit mode
      let materialErrors: string[] = []
      if (!isEditMode && projectMaterials.length > 0) {
        for (const material of projectMaterials) {
          try {
            // Ensure quantityUsed is a proper number and meets validation requirements
            const quantityUsed = parseFloat(material.quantityNeeded.toString())
            
            // Backend validation requires minimum 0.0001
            if (isNaN(quantityUsed) || quantityUsed < 0.0001) {
              console.error(`Invalid quantity for material ${material.materialName}: ${material.quantityNeeded} (must be >= 0.0001)`)
              materialErrors.push(`${material.materialName}: Invalid quantity (${material.quantityNeeded})`)
              continue
            }

            console.log(`Adding material ${material.materialName} with quantity:`, quantityUsed)
            
            const materialData = {
              materialId: material.materialId,
              quantityUsed: quantityUsed,
              notes: material.notes || ''
            }
            
            console.log('Sending material data:', materialData)
            console.log('Project ID:', project.id)
            
            await projectsApi.addMaterial(project.id, materialData)
            
            console.log(`Successfully added material ${material.materialName}`)
          } catch (materialError: any) {
            console.error(`Failed to add material ${material.materialName}:`, materialError)
            
            // Log detailed error information
            if (materialError.response) {
              console.error('Error response data:', materialError.response.data)
              console.error('Error response status:', materialError.response.status)
              console.error('Error response headers:', materialError.response.headers)
              
              // Extract error message for user feedback
              const errorMessage = materialError.response.data?.message || materialError.response.data?.error || 'Unknown error'
              materialErrors.push(`${material.materialName}: ${errorMessage}`)
            } else if (materialError.request) {
              console.error('Error request:', materialError.request)
              materialErrors.push(`${material.materialName}: Network error`)
            } else {
              console.error('Error message:', materialError.message)
              materialErrors.push(`${material.materialName}: ${materialError.message}`)
            }
            
            // Continue with other materials even if one fails
          }
        }
      }

      // Show material errors as warnings but still navigate to success
      if (materialErrors.length > 0) {
        console.warn('Some materials failed to add:', materialErrors)
        setError(`Project ${isEditMode ? 'updated' : 'created'} successfully, but some materials failed to add:\n${materialErrors.join('\n')}`)
        // Don't return here - still navigate to projects list
      }

      // Navigate back to projects list
      navigate('/projects')
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} project:`, error)
      setError(error instanceof Error ? error.message : `Failed to ${isEditMode ? 'update' : 'create'} project`)
    } finally {
      setLoading(false)
    }
  }

  // Get template-specific project name placeholder
  const getProjectNamePlaceholder = (): string => {
    if (!currentTemplate) return "e.g., My Project"
    
    switch (currentTemplate.businessType) {
      case '3d_printing':
        return "e.g., Custom Phone Case Print"
      case 'jewelry':
        return "e.g., Silver Wire Wrapped Earrings"
      case 'woodworking':
        return "e.g., Oak Coffee Table Build"
      case 'photography':
        return "e.g., Wedding Portrait Session"
      case 'automotive':
        return "e.g., Brake Pad Replacement"
      case 'electronics':
        return "e.g., Arduino LED Controller"
      case 'culinary':
        return "e.g., Artisan Sourdough Bread"
      case 'home_brewing':
        return "e.g., IPA Batch #12 - Cascade Hops"
      case 'rc_hobby':
        return "e.g., Custom Racing Drone Build"
      default:
        return `e.g., ${currentTemplate.name} Project`
    }
  }

  // Get template-specific project description
  const getProjectDescription = (): string => {
    if (!currentTemplate) return "Plan your project with material selection and cost calculation"
    
    switch (currentTemplate.businessType) {
      case '3d_printing':
        return "Plan your 3D printing project with filament selection and cost calculation"
      case 'jewelry':
        return "Plan your jewelry project with material selection and cost calculation"
      case 'woodworking':
        return "Plan your woodworking project with lumber and hardware selection and cost calculation"
      case 'photography':
        return "Plan your photography project with equipment and material selection and cost calculation"
      case 'automotive':
        return "Plan your automotive project with parts and material selection and cost calculation"
      case 'electronics':
        return "Plan your electronics project with component selection and cost calculation"
      case 'culinary':
        return "Plan your culinary project with ingredient selection and cost calculation"
      case 'home_brewing':
        return "Set up your brewing recipe with ingredient selection and batch calculation"
      case 'rc_hobby':
        return "Plan your RC project with parts and material selection and cost calculation"
      default:
        return `Plan your ${currentTemplate.name.toLowerCase()} project with material selection and cost calculation`
    }
  }

  // Get template-specific page title
  const getPageTitle = (): string => {
    if (!currentTemplate) return isEditMode ? 'Edit Project' : 'Create New Project'
    
    switch (currentTemplate.businessType) {
      case '3d_printing':
        return isEditMode ? 'Edit Print Job' : 'Create New Print Job'
      case 'jewelry':
        return isEditMode ? 'Edit Jewelry Project' : 'Create New Jewelry Project'
      case 'woodworking':
        return isEditMode ? 'Edit Woodworking Project' : 'Create New Woodworking Project'
      case 'photography':
        return isEditMode ? 'Edit Photo Session' : 'Create New Photo Session'
      case 'automotive':
        return isEditMode ? 'Edit Auto Project' : 'Create New Auto Project'
      case 'electronics':
        return isEditMode ? 'Edit Electronics Project' : 'Create New Electronics Project'
      case 'culinary':
        return isEditMode ? 'Edit Recipe' : 'Create New Recipe'
      case 'home_brewing':
        return isEditMode ? 'Edit Brewing Recipe' : 'Create New Brewing Recipe'
      case 'rc_hobby':
        return isEditMode ? 'Edit RC Build' : 'Create New RC Build'
      default:
        return isEditMode ? `Edit ${currentTemplate.name} Project` : `Create New ${currentTemplate.name} Project`
    }
  }

  // Get template-specific project name label
  const getProjectNameLabel = (): string => {
    if (!currentTemplate) return "Project Name"
    
    switch (currentTemplate.businessType) {
      case '3d_printing':
        return "Print Job Name"
      case 'jewelry':
        return "Project Name"
      case 'woodworking':
        return "Project Name"
      case 'photography':
        return "Session Name"
      case 'automotive':
        return "Project Name"
      case 'electronics':
        return "Project Name"
      case 'culinary':
        return "Recipe Name"
      case 'home_brewing':
        return "Recipe Name"
      case 'rc_hobby':
        return "Build Name"
      default:
        return "Project Name"
    }
  }

  // Get template-specific materials section title
  const getMaterialsSectionTitle = (): string => {
    if (!currentTemplate) return "Materials Required"
    
    switch (currentTemplate.businessType) {
      case '3d_printing':
        return "Filaments & Materials Required"
      case 'jewelry':
        return "Materials Required"
      case 'woodworking':
        return "Lumber & Hardware Required"
      case 'photography':
        return "Equipment & Materials Required"
      case 'automotive':
        return "Parts & Materials Required"
      case 'electronics':
        return "Components Required"
      case 'culinary':
        return "Ingredients Required"
      case 'home_brewing':
        return "Ingredients Required"
      case 'rc_hobby':
        return "Parts & Components Required"
      default:
        return "Materials Required"
    }
  }

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  const handleTemplateFormChange = (field: string, value: any) => {
    setTemplateFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const addMaterial = (material: typeof availableMaterials[0]) => {
    const newMaterial: ProjectMaterial = {
      id: Date.now().toString(),
      materialId: material.id,
      materialName: material.name,
      unitType: material.unitType,
      quantityNeeded: 1,
      estimatedCost: material.avgCost,
      notes: ''
    }
    setProjectMaterials(prev => [...prev, newMaterial])
    setShowMaterialSelector(false)
  }

  const updateMaterial = (id: string, field: keyof ProjectMaterial, value: any) => {
    setProjectMaterials(prev => prev.map(material => {
      if (material.id === id) {
        const updated = { ...material, [field]: value }
        // Recalculate estimated cost when quantity changes
        if (field === 'quantityNeeded') {
          const baseMaterial = availableMaterials.find(m => m.id === material.materialId)
          if (baseMaterial) {
            updated.estimatedCost = value * baseMaterial.avgCost
          }
        }
        return updated
      }
      return material
    }))
  }

  const removeMaterial = (id: string) => {
    setProjectMaterials(prev => prev.filter(material => material.id !== id))
  }

  const totalMaterialsCost = projectMaterials.reduce((sum, material) => {
    const cost = parseFloat(material.estimatedCost?.toString()) || 0
    return sum + cost
  }, 0)
  
  const estimatedLaborCost = (parseFloat(formData.estimatedHours) || 0) * (parseFloat(formData.laborRate) || 0)
  const totalProjectCost = totalMaterialsCost + estimatedLaborCost
  
  // Calculate suggested selling price with healthy profit margins for handmade jewelry
  const suggestedPrice = totalProjectCost > 0 ? totalProjectCost * 2.5 : 0 // 2.5x markup for 60% profit margin
  const alternativePrices = {
    conservative: totalProjectCost * 2.0, // 50% profit margin
    premium: totalProjectCost * 3.0,      // 67% profit margin
    luxury: totalProjectCost * 4.0        // 75% profit margin
  }
  
  const targetPrice = parseFloat(formData.targetPrice) || 0
  const estimatedProfit = targetPrice - totalProjectCost
  const profitMargin = targetPrice > 0 ? (estimatedProfit / targetPrice) * 100 : 0
  
  // Use suggested price if no target price is set
  const displayPrice = targetPrice > 0 ? targetPrice : suggestedPrice
  const displayProfit = displayPrice - totalProjectCost
  const displayMargin = displayPrice > 0 ? (displayProfit / displayPrice) * 100 : 0

  // Debug logging for cost calculations
  console.log('Cost calculation debug:', {
    projectMaterials: projectMaterials.map(m => ({ name: m.materialName, cost: m.estimatedCost, type: typeof m.estimatedCost })),
    totalMaterialsCost,
    estimatedLaborCost,
    totalProjectCost,
    targetPrice,
    displayPrice,
    displayProfit,
    displayMargin
  })

  return (
    <>
      <Helmet>
        <title>Create Project - Jewelry Inventory Tracker</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/projects')}
            leftIcon={<ArrowLeft className="w-4 h-4" />}
          >
            Back to Projects
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {getPageTitle()}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {isEditMode
                ? `Update your ${currentTemplate?.businessType === 'home_brewing' ? 'recipe' : currentTemplate?.businessType === 'culinary' ? 'recipe' : currentTemplate?.businessType === 'photography' ? 'session' : currentTemplate?.businessType === '3d_printing' ? 'print job' : 'project'} details and ${currentTemplate?.businessType === 'home_brewing' || currentTemplate?.businessType === 'culinary' ? 'ingredients' : 'materials'}`
                : getProjectDescription()
              }
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Loading State for Edit Mode */}
        {projectLoading ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jewelry-primary"></div>
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading project data...</span>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
          {/* Project Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Project Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Project Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {getProjectNameLabel()} *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                  placeholder={getProjectNamePlaceholder()}
                />
              </div>

              {/* Project Type */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Project Type *
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowProjectTypeManager(!showProjectTypeManager)}
                    className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                  >
                    {showProjectTypeManager ? 'Hide' : 'Manage'} Project Types
                  </button>
                </div>
                <select
                  id="type"
                  name="type"
                  required
                  value={formData.type}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                >
                  <option value="">Select Project Type</option>
                  {projectTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                
                {/* Project Type Manager */}
                {showProjectTypeManager && (
                  <div className="mt-4 p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                    <ProjectTypeManager
                      onProjectTypeAdded={(projectType) => {
                        // Refresh the project types list
                        fetchProjectTypes();
                        console.log('New project type added:', projectType);
                      }}
                      onProjectTypeUpdated={(projectType) => {
                        // Refresh the project types list
                        fetchProjectTypes();
                        console.log('Project type updated:', projectType);
                      }}
                      onProjectTypeDeleted={(projectTypeId) => {
                        // Refresh the project types list
                        fetchProjectTypes();
                        // If the deleted project type was selected, clear the selection
                        if (formData.type === projectTypeId) {
                          setFormData(prev => ({ ...prev, type: '' }));
                        }
                        console.log('Project type deleted:', projectTypeId);
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Status */}
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status *
                </label>
                <select
                  id="status"
                  name="status"
                  required
                  value={formData.status}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                >
                  <option value="planning">Planning</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="on_hold">On Hold</option>
                </select>
              </div>

              {/* Target Price */}
              <div>
                <label htmlFor="targetPrice" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Target Selling Price ($)
                  {suggestedPrice > 0 && (
                    <span className="text-xs text-blue-600 dark:text-blue-400 ml-2">
                      Suggested: ${suggestedPrice.toFixed(2)}
                    </span>
                  )}
                </label>
                <input
                  type="number"
                  id="targetPrice"
                  name="targetPrice"
                  step="0.01"
                  min="0"
                  value={formData.targetPrice}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                  placeholder={suggestedPrice > 0 ? suggestedPrice.toFixed(2) : "0.00"}
                />
                {suggestedPrice > 0 && !formData.targetPrice && (
                  <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, targetPrice: suggestedPrice.toFixed(2) }))}
                        className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded text-xs hover:bg-blue-200 dark:hover:bg-blue-900/40"
                      >
                        Use ${suggestedPrice.toFixed(2)} (60% margin)
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, targetPrice: alternativePrices.conservative.toFixed(2) }))}
                        className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded text-xs hover:bg-green-200 dark:hover:bg-green-900/40"
                      >
                        ${alternativePrices.conservative.toFixed(2)} (50% margin)
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, targetPrice: alternativePrices.premium.toFixed(2) }))}
                        className="px-2 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded text-xs hover:bg-purple-200 dark:hover:bg-purple-900/40"
                      >
                        ${alternativePrices.premium.toFixed(2)} (67% margin)
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Labor Rate */}
              <div>
                <label htmlFor="laborRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Labor Rate ($/hour)
                </label>
                <input
                  type="number"
                  id="laborRate"
                  name="laborRate"
                  step="0.01"
                  min="0"
                  value={formData.laborRate}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                  placeholder="25.00"
                />
              </div>

              {/* Estimated Hours */}
              <div>
                <label htmlFor="estimatedHours" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Estimated Hours
                </label>
                <input
                  type="number"
                  id="estimatedHours"
                  name="estimatedHours"
                  step="0.25"
                  min="0"
                  value={formData.estimatedHours}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Description */}
            <div className="mt-6">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleFormChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                placeholder="Describe your project design, techniques, or special requirements..."
              />
            </div>
          </div>

          {/* Materials Selection */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {getMaterialsSectionTitle()}
              </h2>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowMaterialSelector(!showMaterialSelector)}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                {currentTemplate?.businessType === 'home_brewing' || currentTemplate?.businessType === 'culinary' ? 'Add Ingredient' : 'Add Material'}
              </Button>
            </div>

            {/* Material Selector */}
            {showMaterialSelector && (
              <div className="mb-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700">
                <h3 className="font-medium text-gray-900 dark:text-white mb-4">
                  {currentTemplate?.businessType === 'home_brewing' || currentTemplate?.businessType === 'culinary' ? 'Select Ingredients from Inventory' : 'Select Materials from Inventory'}
                </h3>
                {materialsLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jewelry-primary mx-auto"></div>
                    <p className="text-gray-600 dark:text-gray-400 mt-2">Loading materials...</p>
                  </div>
                ) : availableMaterials.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <p>No materials available. Add materials to your inventory first.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {availableMaterials.map((material) => (
                      <div
                        key={material.id}
                        onClick={() => addMaterial(material)}
                        className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-white dark:hover:bg-gray-600 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {material.name}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              Stock: {material.currentStock} {material.unitType}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatCurrency(material.avgCost)}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              per {material.unitType}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-4 flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowMaterialSelector(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}

            {/* Selected Materials */}
            {projectMaterials.length > 0 ? (
              <div className="space-y-4">
                {projectMaterials.map((material) => (
                  <div key={material.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
                      {/* Drag Handle */}
                      <div className="md:col-span-2 flex items-center gap-3">
                        <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {material.materialName}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            Unit: {material.unitType}
                          </div>
                        </div>
                      </div>

                      {/* Quantity */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Quantity
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={material.quantityNeeded}
                          onChange={(e) => updateMaterial(material.id, 'quantityNeeded', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-jewelry-primary focus:border-transparent"
                        />
                      </div>

                      {/* Estimated Cost */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Est. Cost
                        </label>
                        <div className="px-2 py-1 text-sm bg-gray-50 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white">
                          {formatCurrency(material.estimatedCost)}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeMaterial(material.id)}
                          className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="mt-3">
                      <input
                        type="text"
                        placeholder="Notes for this material..."
                        value={material.notes || ''}
                        onChange={(e) => updateMaterial(material.id, 'notes', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No materials selected. Click "Add Material" to start building your project.
              </div>
            )}
          </div>

          {/* Cost Summary */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-6">
              <Calculator className="w-5 h-5 text-jewelry-primary" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Cost Analysis
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(totalMaterialsCost)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Materials Cost
                </div>
              </div>
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(estimatedLaborCost)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Labor Cost
                </div>
              </div>
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(totalProjectCost)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Total Cost
                </div>
              </div>
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className={`text-2xl font-bold ${
                  displayProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {formatCurrency(displayProfit)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {targetPrice > 0 ? 'Est. Profit' : 'Suggested Profit'} ({displayMargin.toFixed(1)}%)
                </div>
                {!targetPrice && suggestedPrice > 0 && (
                  <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Based on ${suggestedPrice.toFixed(2)} suggested price
                  </div>
                )}
              </div>
            </div>

            {/* Pricing Guidance */}
            {totalProjectCost > 0 && (
              <div className="mt-4">
                {targetPrice > 0 && profitMargin < 40 && (
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg mb-4">
                    <div className="text-sm text-yellow-800 dark:text-yellow-200">
                      <strong>Low Profit Margin:</strong> Consider increasing your target price.
                      Healthy profit margins for handmade jewelry are typically 50-70%.
                    </div>
                  </div>
                )}
                
                {!targetPrice && suggestedPrice > 0 && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="text-sm text-blue-800 dark:text-blue-200">
                      <strong>Pricing Suggestion:</strong> Based on your costs (${totalProjectCost.toFixed(2)}),
                      we suggest a selling price of <strong>${suggestedPrice.toFixed(2)}</strong> for a healthy 60% profit margin.
                      This accounts for business overhead, time investment, and fair profit.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Project Notes
            </h2>
            <textarea
              id="notes"
              name="notes"
              rows={4}
              value={formData.notes}
              onChange={handleFormChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
              placeholder="Add any additional notes, techniques, or reminders for this project..."
            />
          </div>

          {/* Template-Driven Project Form */}
          <TemplateDrivenProjectForm
            formData={templateFormData}
            onFormChange={handleTemplateFormChange}
          />

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-4 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/projects')}
              leftIcon={<X className="w-4 h-4" />}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              leftIcon={<Save className="w-4 h-4" />}
            >
              {isEditMode ? 'Update Project' : 'Create Project'}
            </Button>
          </div>
        </form>
        )}
      </div>
    </>
  )
}